"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerProps,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { useIsDesktop } from "@/hooks/layout/use-is-desktop";
import { useToggle } from "@/hooks/use-toggle";
import { isNil } from "@/utils/is-nil";
import {
  DialogClose,
  DialogCloseProps,
  DialogTriggerProps,
} from "@radix-ui/react-dialog";
import { usePathname, useRouter } from "next/navigation";
import React, { PropsWithChildren } from "react";

interface Props {
  readonly children: React.ReactNode;
  readonly className?: string;
}

export function ModalHeader(props: Props) {
  const isDesktop = useIsDesktop();
  if (isDesktop) {
    return (
      <DialogHeader className={props.className}>{props.children}</DialogHeader>
    );
  }

  return (
    <DrawerHeader className={props.className}>{props.children}</DrawerHeader>
  );
}

export function ModalTitle(props: Props) {
  const isDesktop = useIsDesktop();
  if (isDesktop) {
    return (
      <DialogTitle className={props.className}>{props.children}</DialogTitle>
    );
  }

  return (
    <DrawerTitle className={props.className}>{props.children}</DrawerTitle>
  );
}

export function ModalDescription(props: Props) {
  const isDesktop = useIsDesktop();
  if (isDesktop) {
    return (
      <DialogDescription className={props.className}>
        {props.children}
      </DialogDescription>
    );
  }

  return (
    <DrawerDescription className={props.className}>
      {props.children}
    </DrawerDescription>
  );
}

export function ModalContent(props: Props) {
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <DialogContent className={props.className}>
        {props.children}
      </DialogContent>
    );
  }

  return (
    <DrawerContent className={props.className}>{props.children}</DrawerContent>
  );
}

export function ModalTrigger(props: DialogTriggerProps) {
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <DialogTrigger
        className={props.className}
        asChild={props.asChild}
        disabled={props.disabled}
      >
        {props.children}
      </DialogTrigger>
    );
  }

  return (
    <DrawerTrigger
      className={props.className}
      asChild={props.asChild}
      disabled={props.disabled}
    >
      {props.children}
    </DrawerTrigger>
  );
}

export function ModalClose(props: DialogCloseProps) {
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <DialogClose className={props.className} asChild={props.asChild}>
        {props.children}
      </DialogClose>
    );
  }

  return (
    <DrawerClose className={props.className} asChild={props.asChild}>
      {props.children}
    </DrawerClose>
  );
}

export function ModalFooter(props: Props) {
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <DialogFooter className={props.className}>{props.children}</DialogFooter>
    );
  }

  return (
    <DrawerFooter className={props.className}>{props.children}</DrawerFooter>
  );
}

interface ModalProps {
  readonly isRoute?: boolean;
  readonly open?: boolean;
  readonly snapPoints?: DrawerProps["snapPoints"];
  readonly redirectTo?: string;

  readonly onOpenChange?: (open: boolean) => void;
  readonly onRouteChange?: () => void;
}

export function Modal({
  children,
  isRoute = true,
  ...props
}: PropsWithChildren<ModalProps>) {
  const router = useRouter();
  const isDesktop = useIsDesktop();
  const modalToggle = useToggle(isRoute);
  const pathname = usePathname();
  const previousPathname = React.useRef<string | null>(null);

  const handleChange = React.useCallback(
    (open: boolean) => {
      if (open || !isRoute) {
        props.onOpenChange?.(open);
        return;
      }

      if (props.redirectTo) {
        router.replace(props.redirectTo);
        return;
      }

      router.back();
    },
    [isRoute, props, router],
  );

  const isOpen = !isNil(props.open)
    ? props.open
    : modalToggle.value || undefined;

  React.useEffect(() => {
    if (previousPathname.current && pathname !== previousPathname.current) {
      props.onRouteChange?.();
    }

    return () => {
      previousPathname.current = pathname;
    };
  }, [pathname, props]);

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={handleChange}>
        {children}
      </Dialog>
    );
  }

  return (
    <Drawer
      open={isOpen}
      onOpenChange={handleChange}
      snapPoints={props.snapPoints}
    >
      {children}
    </Drawer>
  );
}
